import { useState, useEffect, useCallback } from 'react';
import { AcademyProgress } from '../config/academyConfig';

const STORAGE_KEY = 'yourbeans_progress';

const DEFAULT_PROGRESS: AcademyProgress = {
  enrolledAt: new Date().toISOString(),
  modulesCompleted: [],
  currentModule: undefined,
  totalPoints: 0,
  certificateEarned: false
};

export function useProgress() {
  const [progress, setProgress] = useState<AcademyProgress>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          ...DEFAULT_PROGRESS,
          ...parsed
        };
      }
      return DEFAULT_PROGRESS;
    } catch {
      return DEFAULT_PROGRESS;
    }
  });

  // Persist to localStorage whenever progress changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  }, [progress]);

  const completeModule = useCallback((moduleId: string) => {
    setProgress(prev => {
      if (prev.modulesCompleted.includes(moduleId)) {
        return prev;
      }
      return {
        ...prev,
        modulesCompleted: [...prev.modulesCompleted, moduleId]
      };
    });
  }, []);

  const setCurrentModule = useCallback((moduleId: string | undefined) => {
    setProgress(prev => ({
      ...prev,
      currentModule: moduleId
    }));
  }, []);

  const addPoints = useCallback((points: number) => {
    setProgress(prev => ({
      ...prev,
      totalPoints: prev.totalPoints + points
    }));
  }, []);

  const earnCertificate = useCallback(() => {
    setProgress(prev => ({
      ...prev,
      certificateEarned: true
    }));
  }, []);

  const resetProgress = useCallback(() => {
    setProgress(DEFAULT_PROGRESS);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return {
    progress,
    completeModule,
    setCurrentModule,
    addPoints,
    earnCertificate,
    resetProgress
  };
}
